import { Link } from 'react-router-dom'

import type { AdminCreatedStaffInvite, AdminStaffInviteEmail } from '../../api/admin'
import { plural } from './clubOps'

/**
 * Qué pasó DE VERDAD al generar una invitación de staff (mg, 2026-09-10:
 * *"generar link no deja claro cuándo o si mandó el mail"*).
 *
 * El backend encola el mail en `email_outbox` sólo si la invitación trae
 * email, y el despachador manda como mucho uno cada `min_interval_s`
 * (`backend/app/services/outbox.py`). O sea: lo máximo que el sistema sabe
 * acá es "encolado" — nunca "enviado" y muchísimo menos "entregado". Este
 * componente no dice ni una cosa ni la otra; dice encolado, con el freno y
 * el link a /admin/emails, que es donde se ve si salió.
 */
export type MailNoticeTone = 'ok' | 'warn' | 'err' | 'info'

export function staffInviteMailNotice(email: AdminStaffInviteEmail): {
  tone: MailNoticeTone
  text: string
} {
  if (!email.queued) {
    return {
      tone: 'info',
      text:
        'No se mandó ningún mail: la invitación se generó sin email. Copiá el ' +
        'link y mandalo vos.',
    }
  }
  const to = email.recipient ?? 'el destinatario'
  if (!email.transport_configured) {
    return {
      tone: 'err',
      text:
        `Mail encolado para ${to}, pero no hay transporte de mail configurado: ` +
        'no va a salir hasta que aterrice la config. Copiá el link y mandalo vos.',
    }
  }
  if (!email.sending_enabled) {
    return {
      tone: 'warn',
      text:
        `Mail encolado para ${to}, pero el despacho está apagado ` +
        '(EMAIL_SENDING_ENABLED=false): queda en cola hasta que se prenda. ' +
        'Copiá el link y mandalo vos.',
    }
  }
  if (email.min_interval_s <= 0) {
    return {
      tone: 'ok',
      text:
        `Mail encolado para ${to}: sale en el próximo poll del despachador. ` +
        'Que salga no quiere decir que haya llegado.',
    }
  }
  const minutes = Math.round(email.min_interval_s / 60)
  const eta = email.queue_next_send_at
    ? ` El próximo envío de la cola es a las ${formatClock(email.queue_next_send_at)}.`
    : ''
  return {
    tone: 'ok',
    text:
      `Mail encolado para ${to}. Freno global: 1 mail cada ${minutes} min, con ` +
      `${plural(email.queue_pending, 'mail', 'mails')} en cola.${eta} ` +
      'Que salga no quiere decir que haya llegado.',
  }
}

function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { timeStyle: 'short' })
}

const TONE_CLASS: Record<MailNoticeTone, string> = {
  ok: 'banner-ok',
  warn: 'banner-warn',
  err: 'banner-err',
  info: 'banner-info',
}

export function StaffInviteOutcome({
  result,
  onCopy,
  copied = false,
}: {
  result: AdminCreatedStaffInvite
  onCopy: () => void
  copied?: boolean
}) {
  const notice = staffInviteMailNotice(result.email)
  return (
    <div className="mb-md flex flex-col gap-xs">
      <div className={`${TONE_CLASS[notice.tone]} flex flex-col gap-xs`}>
        <span>{notice.text}</span>
        {result.email.queued && (
          <span className="text-xs">
            El estado real del envío se ve en{' '}
            <Link to="/admin/emails" className="underline">
              /admin/emails
            </Link>
            .
          </span>
        )}
      </div>
      <div className="flex items-center gap-sm">
        <input
          type="text"
          readOnly
          value={result.link_url}
          className="input flex-1 font-mono text-xs"
          aria-label="Link de la invitación"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="btn text-xs" onClick={onCopy}>
          {copied ? 'Copiado' : 'Copiar link'}
        </button>
      </div>
    </div>
  )
}
